import React from 'react';
import './DataStructures.css'; // Re-using the same CSS for now

const SystemDesign = () => {
  return (
    <div className="data-structure-section">
      <h2>System Design for Finance</h2>
      <p>
        System design questions for senior roles in investment banking often revolve around building systems that are extremely fast (low latency), can handle a large volume of transactions (high throughput), and are reliable (high availability).
      </p>

      <div className="topic-section">
        <h3>Low Latency Design Principles</h3>
        <p>In domains like high-frequency trading, latency is measured in microseconds or even nanoseconds. Here are key principles to consider:</p>
        <ul>
            <li><strong>Minimize I/O Operations:</strong> Network calls and disk access are major sources of latency. Use in-memory databases/caches, and batch I/O operations where possible.</li>
            <li><strong>Avoid Context Switching:</strong> When a CPU switches from one thread to another, it incurs overhead. Pinning critical threads to specific CPU cores can help.</li>
            <li><strong>Memory Management / Garbage Collection (GC):</strong> In Java, GC pauses can be a huge source of latency.
                <ul>
                    <li>Understand different GC algorithms (e.g., G1GC, ZGC, Shenandoah) and their pause times.</li>
                    <li>Avoid creating unnecessary objects to reduce GC pressure (object pooling can be useful here).</li>
                    <li>Consider off-heap memory storage to bypass GC for certain data.</li>
                </ul>
            </li>
            <li><strong>Network Communication:</strong>
                <ul>
                    <li>Use efficient protocols. TCP with `TCP_NODELAY` is common. UDP can be used for data where some loss is acceptable (like market data feeds).</li>
                    <li>Use efficient data serialization formats like Protocol Buffers or SBE (Simple Binary Encoding) instead of JSON or XML.</li>
                </ul>
            </li>
            <li><strong>Code Optimizations:</strong>
                <ul>
                    <li>Avoid locks and favor non-blocking algorithms (`java.util.concurrent.atomic` package, `ConcurrentHashMap`).</li>
                    <li>Be mindful of cache misses. Structure your data for good memory locality.</li>
                </ul>
            </li>
        </ul>
      </div>

      <div className="topic-section">
        <h3>Example: Designing an Order Matching Engine</h3>
        <p>A classic finance interview question. An order matching engine takes buy and sell orders and matches them to execute trades.</p>
        <h4>Core Components:</h4>
        <ul>
            <li><strong>Order Book:</strong> This is the central data structure. It holds all open buy and sell orders for a particular instrument (e.g., a stock).
                <ul>
                    <li>It's typically implemented with two priority queues (or sorted lists/trees): one for buy orders (bids), sorted from highest price to lowest, and one for sell orders (asks), sorted from lowest price to highest.</li>
                    <li>`TreeMap` in Java can be a good starting point for explaining the concept, as it keeps keys (prices) sorted.</li>
                </ul>
            </li>
            <li><strong>Matching Logic:</strong> When a new order comes in, the engine checks the opposite side of the order book.
                <ul>
                    <li>A new buy order is matched against the sell orders, starting with the lowest price.</li>
                    <li>A new sell order is matched against the buy orders, starting with the highest price.</li>
                </ul>
            </li>
            <li><strong>Persistence:</strong> The state of the order book and executed trades must be persisted to disk for recovery. This needs to be done without sacrificing too much performance (e.g., using asynchronous logging or event sourcing).</li>
        </ul>
        <p>The key challenge is to make the matching process extremely fast and thread-safe. A single-threaded design for the matching logic of a single instrument is a common pattern to avoid locking complexity, with different instruments handled by different threads.</p>
      </div>

    </div>
  );
};

export default SystemDesign;